/**
 * The contract editor's header (status chip, save, promote, review
 * picker, PDF actions) and the frozen-PDF preview. Shared by the legacy
 * template editor and the beta (document-IR) editor so both lanes walk the
 * same edit → granska → sign steps.
 */

import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Download, Eye, MoreHorizontal } from 'lucide-react';
import { effectiveSigningMode, type TemplateModule } from '@hyranai/regta-templates';
import { tenantApi, type ContractDetail } from '../api.js';
import { SigningModeBadge } from './SigningModeBadge.js';
import { locateAnchor, setLocateHover, type LocateKind } from '../lib/locate-anchor.js';

export type SaveState = 'idle' | 'saving' | 'error';

const STATUS_LABEL: Record<ContractDetail['status'], string> = {
  draft: 'Utkast',
  pending: 'Klar för granskning',
  in_review: 'Under granskning',
  awaiting_manual_signature: 'Väntar på underskrift',
  sent: 'Skickat',
  viewed: 'Öppnat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

/**
 * One entry in the review-readiness summary (REGTA-49). Hover outlines
 * the target editor control via its `data-locate-anchor`; click hands
 * off to the page so it can switch tabs before scrolling.
 */
function MissingEntryLink({
  label,
  title,
  anchors,
  onLocate,
}: {
  label: string;
  title: string;
  anchors: ReadonlyArray<string>;
  onLocate: (anchors: ReadonlyArray<string>) => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={() => {
        setLocateHover(anchors, false);
        onLocate(anchors);
      }}
      onMouseEnter={() => setLocateHover(anchors, true)}
      onMouseLeave={() => setLocateHover(anchors, false)}
      onFocus={() => setLocateHover(anchors, true)}
      onBlur={() => setLocateHover(anchors, false)}
      className="cursor-pointer rounded-sm underline decoration-amber-500/60 decoration-dotted underline-offset-2 hover:bg-amber-100 hover:decoration-amber-700 hover:decoration-solid focus-visible:ring-1 focus-visible:ring-amber-500 focus-visible:outline-none"
    >
      {label}
    </button>
  );
}

export function StatusBar({
  token,
  contract,
  dirty,
  saveState,
  onSave,
  onPromote,
  onRevertToDraft,
  onArchive,
  onRequestReview,
  onCancelReview,
  isActiveReviewer,
  activeReviewerName,
  lastClosedReviewStatus,
  missing,
  tmpl,
  editable,
  onProceedToSigning,
  onLocateMissing,
  badge,
}: {
  token: string;
  contract: ContractDetail;
  dirty: boolean;
  saveState: SaveState;
  onSave: () => void;
  onPromote: () => void;
  onRevertToDraft: () => Promise<void> | void;
  onArchive: () => Promise<void> | void;
  onRequestReview: (
    reviewerId: string,
    comment: string | undefined,
  ) => Promise<void>;
  /** Null when there is no open review to cancel. */
  onCancelReview: (() => Promise<void> | void) | null;
  isActiveReviewer: boolean;
  activeReviewerName: string | null;
  /** Status of the most recent closed (non-pending) review row, or null. */
  lastClosedReviewStatus: 'approved' | 'rejected' | 'cancelled' | null;
  missing: {
    fields: string[];
    slots: string[];
    attachmentSlots: string[];
  } | null;
  tmpl: TemplateModule;
  /** True when the actor can edit (draft, or reviewer on `in_review`). */
  editable: boolean;
  /** Reveal the Signering tab + switch to it (pre-sign decision row). */
  onProceedToSigning: () => void;
  /** REGTA-49 — scroll the editor to a missing entry's control. */
  onLocateMissing: (
    kind: LocateKind,
    id: string,
    anchors: ReadonlyArray<string>,
  ) => void;
  /** Extra chip beside the status (the beta lane's "Beta" tag). */
  badge?: ReactNode;
}) {
  const status = contract.status;
  const isDraft = status === 'draft';
  const isPending = status === 'pending';
  const isCancelled = status === 'cancelled';
  const isInReview = status === 'in_review';
  const canPromote = isDraft && !dirty && saveState !== 'saving';
  const [picker, setPicker] = useState(false);
  // Reflect the most recent review's outcome on the pending chip —
  // "Granskning klar" (approved) vs the default "Klar för granskning".
  const lastReviewApproved = lastClosedReviewStatus === 'approved';
  // Effective signing mode (per ADR-0061) — resolves `signingOptional`
  // templates from `values.include_signing`.
  const effectiveSigning = effectiveSigningMode(
    tmpl.metadata,
    contract.values as Record<string, unknown>,
  );

  const fieldLabel = (key: string): string => {
    const f = tmpl.variableSchema.find((v) => v.key === key);
    return f?.label ?? key;
  };
  const slotLabel = (id: string): string => {
    const s = tmpl.metadata.partySlots.find((sl) => sl.id === id);
    return s?.label ?? id;
  };
  const attachmentSlotLabel = (id: string): string => {
    const s = (tmpl.metadata.attachmentSlots ?? []).find((sl) => sl.id === id);
    return s ? `Bilaga ${s.fixedNumber} – ${s.label}` : id;
  };
  // Anchor candidates for a missing field. Party-autofilled fields have
  // no editor control while their slot is empty — fall back to the slot
  // card so the click still lands somewhere useful.
  const fieldAnchors = (key: string): string[] => {
    const anchors = [locateAnchor('field', key)];
    const af = tmpl.variableSchema.find((v) => v.key === key)?.autofill;
    if (af?.kind === 'party') {
      const slot = tmpl.metadata.partySlots.find(
        (s) => s.role === af.role && s.ordinal === af.ordinal,
      );
      if (slot) anchors.push(locateAnchor('slot', slot.id));
    }
    return anchors;
  };

  // Bold visual switch when the contract has unsaved edits — amber rail
  // on the left edge + warm tint replaces the neutral stone shell. Only
  // engages while the actor can actually save; read-only viewers see the
  // calm default chrome.
  const showUnsaved = dirty && editable;
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iP(hone|od|ad)/.test(navigator.platform);
  const saveShortcut = isMac ? '⌘S' : 'Ctrl+S';

  // Long contracts scroll the StatusBar off-screen. Track its viewport
  // visibility so the floating save chip below only shows up when the
  // user has actually lost sight of the header — no double-render when
  // both are in view.
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerInView, setHeaderInView] = useState(true);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setHeaderInView(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-8px 0px 0px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const showFloatingSave = showUnsaved && !headerInView;

  return (
    <>
    <div
      ref={headerRef}
      className={
        'mb-4 flex flex-col gap-2 rounded border px-4 py-3 transition-colors duration-200 ' +
        (showUnsaved
          ? 'border-amber-300 bg-amber-50/60 shadow-[inset_4px_0_0_0_var(--color-amber-500)]'
          : 'border-stone-200 bg-white')
      }
    >
      <h1 className="truncate text-base font-semibold text-stone-900" title={contract.title}>
        {contract.title}
      </h1>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip status={status} reviewed={lastReviewApproved} />
          <SigningModeBadge signingMode={effectiveSigning} />
          {badge}
          {showUnsaved && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white/80 px-2 py-0.5 text-[10px] font-medium tracking-wider text-amber-800 uppercase"
              role="status"
              aria-live="polite"
            >
              <span
                aria-hidden="true"
                className="regta-unsaved-dot inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
              />
              Osparade ändringar
            </span>
          )}
          {saveState === 'error' && (
            <span className="text-xs text-red-700">Sparning misslyckades</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ContractPdfButton token={token} contract={contract} />
          {editable && (
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || saveState === 'saving'}
              title={dirty ? `Spara (${saveShortcut})` : 'Inga osparade ändringar'}
              aria-keyshortcuts={isMac ? 'Meta+S' : 'Control+S'}
              className={
                'inline-flex items-center gap-2 rounded px-3 py-1.5 text-xs font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
                (showUnsaved && saveState !== 'saving'
                  ? 'regta-save-cta bg-amber-600 hover:bg-amber-700 focus:ring-2 focus:ring-amber-400 focus:outline-none'
                  : 'bg-stone-900 hover:bg-stone-800')
              }
            >
              <span>{saveState === 'saving' ? 'Sparar…' : 'Spara'}</span>
              {showUnsaved && saveState !== 'saving' && (
                <kbd className="hidden rounded border border-amber-300/70 bg-amber-700/40 px-1 py-px font-mono text-[10px] leading-none text-amber-50 sm:inline-block">
                  {saveShortcut}
                </kbd>
              )}
            </button>
          )}
          {isDraft && (
            <button
              type="button"
              onClick={onPromote}
              disabled={!canPromote}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
              title={
                !canPromote
                  ? 'Spara först innan avtalet markeras som klart för granskning.'
                  : undefined
              }
            >
              Markera klar för granskning
            </button>
          )}
          {(isPending || isCancelled) && (
            <button
              type="button"
              onClick={() => void onRevertToDraft()}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-800 hover:bg-stone-50"
              title={
                isPending
                  ? 'Lås upp för redigering — fältkraven gäller fortfarande.'
                  : 'Lämna signeringen och gör avtalet redigerbart igen. Ett nytt dokument skapas vid nästa utskick.'
              }
            >
              {isPending ? 'Återställ till utkast' : 'Återgå till utkast'}
            </button>
          )}
          {isInReview && onCancelReview && !isActiveReviewer && (
            <button
              type="button"
              onClick={() => void onCancelReview()}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-800 hover:bg-stone-50"
              title="Avbryt den pågående granskningen och återgå till klart-läge."
            >
              Avbryt granskning
            </button>
          )}
          {(isDraft || isPending || isInReview) && (
            <button
              type="button"
              onClick={() => void onArchive()}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
              title="Arkivera avtalet. Endast tillgängligt innan signering har påbörjats."
            >
              Arkivera
            </button>
          )}
        </div>
      </div>

      {/* Decision row — the two paths out of a ready contract sit on
          their own line with distinct colours: review (valfritt, purple)
          vs. proceed to signing (indigo). Review stays skippable; signing
          options only surface once the user proceeds. */}
      {(isPending || isCancelled) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-stone-200 pt-2.5">
          <span className="mr-1 text-[11px] font-medium tracking-wide text-stone-500 uppercase">
            Nästa steg
          </span>
          {isPending &&
            (lastReviewApproved ? (
              <PendingOverflowMenu
                onRequestAnotherReview={() => setPicker(true)}
                pickerOpen={picker}
                onClose={() => setPicker(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setPicker((p) => !p)}
                className="rounded border border-purple-300 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-800 transition-colors hover:bg-purple-100"
              >
                {picker ? 'Stäng' : 'Skicka för granskning'}
              </button>
            ))}
          <button
            type="button"
            onClick={onProceedToSigning}
            className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
          >
            {effectiveSigning === 'export_only'
              ? 'Fortsätt till export'
              : 'Fortsätt till signering'}
            <span aria-hidden="true">→</span>
          </button>
          {isPending && (
            <span className="text-[11px] text-stone-400">Granskning är valfri.</span>
          )}
        </div>
      )}

      {isInReview && (
        <div className="rounded border border-purple-200 bg-purple-50 p-2 text-xs text-purple-900">
          {isActiveReviewer
            ? 'Du granskar detta avtal — godkänn eller begär ändring i panelen till vänster.'
            : `Avtalet ligger ute för granskning hos ${activeReviewerName ?? 'granskare'}.`}
        </div>
      )}

      {isPending && picker && (
        <RequestReviewInline
          token={token}
          contractId={contract.id}
          onSubmit={async (reviewerId, comment) => {
            await onRequestReview(reviewerId, comment);
            setPicker(false);
          }}
          onCancel={() => setPicker(false)}
        />
      )}

      {missing &&
        (missing.fields.length > 0 ||
          missing.slots.length > 0 ||
          missing.attachmentSlots.length > 0) && (
          <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            <div className="mb-1 font-medium">
              Saknas innan avtalet kan markeras klart för granskning:
            </div>
            {missing.slots.length > 0 && (
              <div>
                Parter:{' '}
                {missing.slots.map((id, i) => (
                  <Fragment key={id}>
                    {i > 0 && ', '}
                    <MissingEntryLink
                      label={slotLabel(id)}
                      title="Visa parten i redigeraren"
                      anchors={[locateAnchor('slot', id)]}
                      onLocate={(anchors) => onLocateMissing('slot', id, anchors)}
                    />
                  </Fragment>
                ))}
              </div>
            )}
            {missing.fields.length > 0 && (
              <div>
                Fält:{' '}
                {missing.fields.map((key, i) => (
                  <Fragment key={key}>
                    {i > 0 && ', '}
                    <MissingEntryLink
                      label={fieldLabel(key)}
                      title="Visa fältet i redigeraren"
                      anchors={fieldAnchors(key)}
                      onLocate={(anchors) => onLocateMissing('field', key, anchors)}
                    />
                  </Fragment>
                ))}
              </div>
            )}
            {missing.attachmentSlots.length > 0 && (
              <div>
                Bilagor:{' '}
                {missing.attachmentSlots.map((id, i) => (
                  <Fragment key={id}>
                    {i > 0 && ', '}
                    <MissingEntryLink
                      label={attachmentSlotLabel(id)}
                      title="Visa bilagan under fliken Bilagor"
                      anchors={[locateAnchor('attachment_slot', id)]}
                      onLocate={(anchors) =>
                        onLocateMissing('attachment_slot', id, anchors)
                      }
                    />
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        )}
    </div>

    {/* Floating save chip — surfaces the unsaved-state CTA when the
        StatusBar has scrolled out of view. Hidden in <print> via the
        global @media print rule already in place. */}
    {showFloatingSave && (
      <div
        data-no-print
        className="regta-save-fab pointer-events-none fixed right-4 bottom-4 z-50 sm:right-6 sm:bottom-6"
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-amber-300 bg-white/95 py-1.5 pr-1.5 pl-3 shadow-lg shadow-amber-900/10 backdrop-blur">
          <span
            aria-hidden="true"
            className="regta-unsaved-dot inline-block h-2 w-2 rounded-full bg-amber-500"
          />
          <span className="text-[10px] font-medium tracking-wider text-amber-800 uppercase">
            Osparade ändringar
          </span>
          <button
            type="button"
            onClick={onSave}
            disabled={saveState === 'saving'}
            title={`Spara (${saveShortcut})`}
            aria-keyshortcuts={isMac ? 'Meta+S' : 'Control+S'}
            className={
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ' +
              (saveState === 'saving'
                ? 'bg-stone-900'
                : 'regta-save-cta bg-amber-600 hover:bg-amber-700 focus:ring-2 focus:ring-amber-400 focus:outline-none')
            }
          >
            <span>{saveState === 'saving' ? 'Sparar…' : 'Spara'}</span>
            {saveState !== 'saving' && (
              <kbd className="hidden rounded border border-amber-300/70 bg-amber-700/40 px-1 py-px font-mono text-[10px] leading-none text-amber-50 sm:inline-block">
                {saveShortcut}
              </kbd>
            )}
          </button>
        </div>
      </div>
    )}
    </>
  );
}

/**
 * Overflow menu shown on a `pending` contract that already cleared one
 * review round. Lets the requester start another review round without
 * the prominent CTA tempting them into duplicate sends.
 */
function PendingOverflowMenu({
  onRequestAnotherReview,
  pickerOpen,
  onClose,
}: {
  onRequestAnotherReview: () => void;
  pickerOpen: boolean;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => {
          if (pickerOpen) {
            onClose();
            return;
          }
          setOpen((o) => !o);
        }}
        aria-expanded={open || pickerOpen}
        aria-haspopup="menu"
        aria-label={
          pickerOpen ? 'Stäng granskningspanel' : 'Fler granskningsåtgärder'
        }
        title={
          pickerOpen ? 'Stäng granskningspanel' : 'Fler granskningsåtgärder'
        }
        className="inline-flex h-7 w-7 items-center justify-center rounded border border-stone-300 bg-white text-stone-700 hover:bg-stone-50"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 z-40 mt-1.5 w-56 rounded-md border border-stone-200 bg-white p-1 shadow-lg shadow-stone-900/5"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onRequestAnotherReview();
            }}
            className="block w-full rounded px-2 py-1.5 text-left text-xs text-stone-800 hover:bg-stone-50"
          >
            Skicka för granskning igen
          </button>
        </div>
      )}
    </div>
  );
}

export function StatusChip({
  status,
  reviewed,
}: {
  status: ContractDetail['status'];
  /** True when the most recent review on this contract is `approved` —
   *  flips the `pending` chip to a green "Granskning klar" label. */
  reviewed?: boolean;
}) {
  const map: Record<ContractDetail['status'], string> = {
    draft: 'bg-stone-100 text-stone-700 border-stone-300',
    pending: 'bg-blue-50 text-blue-800 border-blue-300',
    in_review: 'bg-purple-50 text-purple-800 border-purple-300',
    awaiting_manual_signature: 'bg-indigo-50 text-indigo-800 border-indigo-300',
    sent: 'bg-indigo-50 text-indigo-800 border-indigo-300',
    viewed: 'bg-indigo-50 text-indigo-800 border-indigo-300',
    awaiting_signature: 'bg-indigo-50 text-indigo-800 border-indigo-300',
    signed: 'bg-green-50 text-green-800 border-green-300',
    archived: 'bg-stone-100 text-stone-500 border-stone-300',
    cancelled: 'bg-red-50 text-red-800 border-red-300',
  };
  const isReviewedPending = status === 'pending' && reviewed;
  const cls = isReviewedPending
    ? 'bg-green-50 text-green-800 border-green-300'
    : map[status];
  const label = isReviewedPending ? 'Granskning klar' : STATUS_LABEL[status];
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}

/**
 * Inline reviewer picker, rendered below the StatusBar when the user
 * clicks "Skicka för granskning". Loads eligible reviewers on mount
 * (group admins of the contract's group).
 */
function RequestReviewInline({
  token,
  contractId,
  onSubmit,
  onCancel,
}: {
  token: string;
  contractId: string;
  onSubmit: (reviewerId: string, comment: string | undefined) => Promise<void>;
  onCancel: () => void;
}) {
  const [reviewers, setReviewers] = useState<
    null | Array<{ id: string; name: string | null; email: string }>
  >(null);
  const [reviewerId, setReviewerId] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    tenantApi.reviews
      .list_potential_reviewers({ contract_id: contractId })
      .then((r) => {
        if (cancelled) return;
        setReviewers(r.reviewers);
        if (r.reviewers[0]) setReviewerId(r.reviewers[0].id);
      })
      .catch((e) => !cancelled && setErr((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [token, contractId]);

  const submit = async () => {
    if (!reviewerId) return;
    setSubmitting(true);
    setErr(null);
    try {
      await onSubmit(reviewerId, comment.trim() ? comment.trim() : undefined);
    } catch (e) {
      setErr((e as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded border border-purple-200 bg-purple-50 p-3">
      <div className="mb-2 text-xs font-medium text-purple-900">
        Skicka avtalet till en granskare innan signering.
      </div>
      {reviewers === null ? (
        <div className="text-xs text-stone-500">Laddar granskare…</div>
      ) : reviewers.length === 0 ? (
        <div className="text-xs text-stone-700">
          Inga gruppadministratörer finns att välja. Be en
          organisationsadministratör att utse en gruppadmin.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <label className="text-[11px] uppercase tracking-wider text-stone-500">
            Granskare
          </label>
          <select
            value={reviewerId}
            onChange={(e) => setReviewerId(e.target.value)}
            className="rounded border border-stone-300 bg-white px-2 py-1 text-sm"
            disabled={submitting}
          >
            {reviewers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name ?? u.email}
                {u.name ? ` (${u.email})` : ''}
              </option>
            ))}
          </select>
          <label className="text-[11px] uppercase tracking-wider text-stone-500">
            Kommentar (valfri)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Lägg gärna en kommentar till granskaren"
            className="min-h-[64px] rounded border border-stone-300 bg-white px-2 py-1 text-sm"
            disabled={submitting}
          />
          {err && <div className="text-xs text-red-700">{err}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitting || !reviewerId}
              className="rounded bg-purple-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-800 disabled:opacity-50"
            >
              {submitting ? 'Skickar…' : 'Skicka'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
            >
              Avbryt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "View final PDF" — fetches the server-rendered Gotenberg PDF and opens
 * it in a same-tab full-screen modal with an iframe pointed at the blob
 * URL. The blob URL is revoked when the modal closes.
 */
/**
 * Header action for the contract's rendered PDF. Server-side `render`
 * dispatches on status (live SSR for drafts, frozen sent/signed PDFs
 * later), so the label + behaviour follow suit:
 *   - signed   → "Ladda ner avtal" — downloads the final signed PDF.
 *   - anything else → "Visa preliminär PDF" — opens the not-yet-final
 *     document in a modal preview.
 */
export function ContractPdfButton({
  token,
  contract,
}: {
  token: string;
  contract: ContractDetail;
}) {
  const isSigned = contract.status === 'signed';
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const blob = await tenantApi.contracts.render(contract.id);
      const url = URL.createObjectURL(blob);
      if (isSigned) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${contract.title || 'avtal'}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        setPdfUrl(url);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token, contract.id, contract.title, isSigned]);

  const close = useCallback(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  }, [pdfUrl]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const label = loading
    ? isSigned
      ? 'Hämtar…'
      : 'Genererar PDF…'
    : isSigned
      ? 'Ladda ner avtal'
      : 'Visa preliminär PDF';

  return (
    <>
      <button
        type="button"
        onClick={run}
        disabled={loading}
        className="inline-flex items-center gap-1.5 rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-800 transition-colors hover:bg-stone-50 disabled:opacity-60"
      >
        {isSigned ? (
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {label}
      </button>
      {err && (
        <span className="text-xs text-red-700" title={err}>
          PDF-fel
        </span>
      )}
      {pdfUrl && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/60 p-6"
          role="dialog"
          aria-modal="true"
        >
          <div className="mb-2 flex items-center justify-between text-white">
            <span className="text-sm">
              Preliminär PDF — så här kommer det färdiga dokumentet att se ut
            </span>
            <button
              type="button"
              onClick={close}
              className="rounded bg-white/20 px-3 py-1 text-xs hover:bg-white/30"
            >
              Stäng
            </button>
          </div>
          <iframe
            src={pdfUrl}
            title="Preliminär PDF"
            className="flex-1 rounded border-0 bg-white"
          />
        </div>
      )}
    </>
  );
}

/**
 * Inline PDF preview for post-draft statuses (per ADR-0024 §"Contract
 * fixing"). Fetches the contract's render endpoint, which dispatches
 * server-side on status: draft = live SSR, sent+ = frozen sent.pdf,
 * signed = signed.pdf. The bytes are wrapped in a blob URL so the
 * browser's PDF viewer takes over.
 */
export function FrozenPdfPreview({
  token,
  contractId,
}: {
  token: string;
  contractId: string;
}) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    setErr(null);
    setPdfUrl(null);
    (async () => {
      try {
        const blob = await tenantApi.contracts.render(contractId);
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setPdfUrl(blobUrl);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [token, contractId]);

  if (err) {
    return (
      <div className="m-4 rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700">
        Kunde inte ladda PDF: {err}
      </div>
    );
  }
  if (!pdfUrl) {
    return (
      <div className="flex flex-1 items-center justify-center text-xs text-stone-500">
        Laddar PDF…
      </div>
    );
  }
  return (
    <iframe
      src={pdfUrl}
      title="Avtal PDF"
      className="flex-1 border-0 bg-white"
    />
  );
}
